import React from "react";
import PropTypes from "prop-types";
import ChatBotContainer from "../components/ChatBotContainer";
import { LoadingSpinner } from "../components/common/ui/LoadingSpinner";
import { AcknowledgmentModal } from "../components/common/ui/AcknowledgmentModal";
import { User, Sun, Moon } from "lucide-react";

/* ========== Theme Toggle ========== */
function ThemeToggle({ isDark, onToggle }) {
    return (
        <button
            onClick={onToggle}
            aria-label="Toggle theme"
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className={`p-2 rounded-full transition-colors ${isDark
                    ? "bg-gray-800 text-yellow-400 hover:bg-gray-700"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
        >
            {isDark ? <Sun size={18} /> : <Moon size={18} className="text-gray-500" />}
        </button>
    );
}
ThemeToggle.propTypes = {
    isDark: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};
/* ========== Header ========== */
function Header({ isDark, onThemeToggle }) {
    const userName = localStorage.getItem("LoginUser_name") || "User";

    return (
        <header className="sticky top-0 z-50 min-h-14 safe-top bg-white dark:bg-gray-800 shadow-sm transition-colors">
            <div className="mx-auto flex h-full items-center justify-between px-4 md:px-6">
                <span className="text-sm sm:text-base md:text-lg  font-semibold text-gray-800 dark:text-white">
                    Welcome to HDX AI
                </span>

                <div className="flex items-center gap-3">
                    <ThemeToggle isDark={isDark} onToggle={onThemeToggle} />
                    <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                        <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200">
                            {userName}
                        </span>
                    </div>
                </div>
            </div>
        </header>
    );
}
Header.propTypes = {
    isDark: PropTypes.bool.isRequired,
    onThemeToggle: PropTypes.func.isRequired,
};

/* ========== Chatbot Screen Base ========== */
export function ChatbotScreenBase({
    isDarkTheme,
    toggleTheme,
    loading,
    chatModesMap,
    activeMode,
    systemContext,
    showModal,
    modalMessage,
    setShowModal,
}) {
    return (
        <div className="flex h-[100svh] overflow-hidden flex-col bg-gray-50 dark:bg-gray-900 transition-colors overscroll-contain">
            <Header isDark={isDarkTheme} onThemeToggle={toggleTheme} />

            {/* Main Chat Section */}
            <main className="flex-1 overflow-y-auto momentum-scroll bg-white dark:bg-gray-800 transition-all duration-300 ease-in-out">
                {loading ? (
                    <div className="flex h-full items-center justify-center">
                        <LoadingSpinner />
                    </div>
                ) : (
                    <div className="h-[calc(100dvh-3.5rem)]">
                        {activeMode && Object.keys(chatModesMap).length > 0 && (
                            <ChatBotContainer
                                initialMode={activeMode}
                                chatModesMap={chatModesMap}
                                companyCode={systemContext.companyCode}
                                regionCode={systemContext.regionCode}
                                userCode={systemContext.userCode}
                            />
                        )}
                    </div>
                )}
            </main>

            {/* Modal */}
            <AcknowledgmentModal
                isOpen={showModal}
                message={modalMessage}
                onClose={() => setShowModal(false)}
            />
        </div>
    );
}

ChatbotScreenBase.propTypes = {
    isDarkTheme: PropTypes.bool.isRequired,
    toggleTheme: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    chatModesMap: PropTypes.object,
    activeMode: PropTypes.string,
    systemContext: PropTypes.shape({
        companyCode: PropTypes.string,
        regionCode: PropTypes.string,
        userCode: PropTypes.string,
    }).isRequired,
    showModal: PropTypes.bool,
    modalMessage: PropTypes.string,
    setShowModal: PropTypes.func,
};
